import styled from "styled-components";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { Outlet } from "react-router-dom";
import { RetractableMenuProvider } from "../context/RetractableMenuContext";
import { useEffect } from "react";

const StyledAppLayout = styled.div`
  display: grid;
  height: 100dvh;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto 1fr;

  /* Medium devices (landscape tablets, 768px and up) */
  @media only screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
`;

const Main = styled.main`
  overflow-y: auto;

  background-color: var(--color-background);

  /* Medium devices (landscape tablets, 768px and up) */
  @media only screen and (max-width: 768px) {
    flex: 1;
  }
`;

const Container = styled.div`
  max-width: 45rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 3.2rem;
  height: 100%;
`;

/**
 * AppLayout Component
 * 
 * This component defines the overall layout structure of the application,
 * including a header, sidebar and a main content area. It also provides
 * context for managing the retractable menu state
 * 
 * Structure:
 * - Wraps the entire application in a styled layout (`StyledAppLayout`)
 * - Includes the `RetractableMenuProvider` to manage the state of a retractable menu.
 * - Renders the `Header` and `Sidebar` components for consistent navigation.
 * - Defines a `Main` section where the dynamic content of the application is displayed using `Outlet`
 * 
 * @returns {JSX.Element} The AppLayout component
 */
function AppLayout() {
  useEffect(() => {
    function updateViewportHeight() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    }
    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);
    return () => {
      window.removeEventListener("resize", updateViewportHeight);
    };
  }, []);
  return (
    <StyledAppLayout>
      {/* Context for the retractable menu */}
      <RetractableMenuProvider>
        {/* Renders the application header */}
        <Header />

        {/* Renders the sidebar navigation */}
        <Sidebar />
      </RetractableMenuProvider>

      {/* Main content area */}
      <Main>
        <Container>
          {/* Renders child routes or components */}
          <Outlet />
        </Container>
      </Main>
    </StyledAppLayout>
  );
}

export default AppLayout;
